/**
 * Real-time listener with polling fallback when Firestore Listen fails (e.g. CORS).
 * Use this instead of onSnapshot from 'firebase/firestore'. Cost: 1 read per doc change (real-time or polling).
 * When Firestore is configured for forced long-polling (Safari/staging), we use app-level getDoc/getDocs
 * polling (or one-time read when REACT_APP_DISABLE_REALTIME_IN_STAGING and non-essential). Firestore may
 * still use /Listen/channel in long-polling mode (e.g. TYPE=xmlhttp). options.purpose, options.component, options.essential.
 */
import { onSnapshot as firebaseOnSnapshot, getDoc, getDocs } from 'firebase/firestore';

const POLL_INTERVAL_MS = 5000; // fallback and forced-polling interval
/** After one Listen error in this session, use polling for all listeners (avoid retries). */
let realTimeFailedThisSession = false;

const isStaging = typeof window !== 'undefined' && (window.location.hostname || '').includes('staging');
const disableRealtimeInStaging = isStaging && String(process.env.REACT_APP_DISABLE_REALTIME_IN_STAGING || '').toLowerCase() === 'true';
/** When true, use getDoc/getDocs polling (or one-time read for non-essential when disable flag set). Set by firebase.js for Safari+staging. */
function forcePollingOnly() {
  if (typeof window !== 'undefined' && window.__ARTMO_FIRESTORE_FORCE_POLLING) return true;
  return isStaging || (typeof navigator !== 'undefined' && /Safari/.test(navigator.userAgent || '') && !/Chrome/.test(navigator.userAgent || ''));
}

function isListenError(err) {
  if (!err) return false;
  const msg = String(err?.message || '').toLowerCase();
  const code = err?.code;
  return (
    msg.includes('access control') ||
    msg.includes('load failed') ||
    msg.includes('failed to fetch') ||
    code === 'unavailable' ||
    code === 'resource-exhausted'
  );
}

function isDocumentReference(ref) {
  return ref && typeof ref.id === 'string' && typeof ref.path === 'string';
}

function startPolling(reference, onNext, onError, cancelledRef) {
  const poll = () => {
    if (cancelledRef.current) return;
    const p = isDocumentReference(reference) ? getDoc(reference) : getDocs(reference);
    p.then((snap) => {
      if (!cancelledRef.current) onNext(snap);
    }).catch((e) => {
      if (!cancelledRef.current && onError) onError(e);
    });
  };
  poll();
  return setInterval(poll, POLL_INTERVAL_MS);
}

/**
 * Same API as Firestore onSnapshot. Prefer real-time (or SDK long-polling); fall back to app polling on Listen error.
 * options.purpose, options.component (for staging logs), options.essential (keep realtime when REACT_APP_DISABLE_REALTIME_IN_STAGING is set).
 */
export function onSnapshot(reference, onNext, onError, options) {
  const opts = options || {};
  const purpose = opts.purpose || 'realtime';
  const component = opts.component || purpose;
  const essential = opts.essential === true;
  const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
  const polling = forcePollingOnly() || realTimeFailedThisSession;
  const oneTimeOnly = disableRealtimeInStaging && !essential;

  if (isStaging) {
    console.log('[firestore] listener', { component, purpose, route: pathname, pollingOnly: polling, oneTimeOnly });
  }

  let unsub = null;
  let pollTimer = null;
  const cancelled = { current: false };

  const cleanup = () => {
    cancelled.current = true;
    if (unsub) {
      try { unsub(); } catch (_) {}
      unsub = null;
    }
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  if (oneTimeOnly) {
    const p = isDocumentReference(reference) ? getDoc(reference) : getDocs(reference);
    p.then((snap) => { if (!cancelled.current) onNext(snap); }).catch((e) => { if (!cancelled.current && onError) onError(e); });
    return cleanup;
  }

  if (polling) {
    pollTimer = startPolling(reference, onNext, onError, cancelled);
    return cleanup;
  }

  const errorHandler = (err) => {
    if (isListenError(err)) {
      realTimeFailedThisSession = true;
      if (unsub) {
        try { unsub(); } catch (_) {}
        unsub = null;
      }
      pollTimer = startPolling(reference, onNext, onError, cancelled);
    } else if (onError) {
      onError(err);
    }
  };

  try {
    unsub = firebaseOnSnapshot(reference, onNext, errorHandler);
  } catch (e) {
    if (isListenError(e)) {
      realTimeFailedThisSession = true;
      pollTimer = startPolling(reference, onNext, onError, cancelled);
    } else {
      throw e;
    }
  }

  return cleanup;
}
